import { Box, Button, Checkbox, Container, Divider, FormControlLabel, Grid, Paper, Stack, TextField, Typography, Stepper, Step, StepButton, RadioGroup, Radio, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useState, useRef, type ChangeEvent } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import TurnstileWidget from "../components/TurnstileWidget";
import { buildContactAttachments, submitContact } from "../utils/contact";

import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ElectricBoltRoundedIcon from '@mui/icons-material/ElectricBoltRounded';
import BatteryChargingFullRoundedIcon from '@mui/icons-material/BatteryChargingFullRounded';
import HandymanRoundedIcon from '@mui/icons-material/HandymanRounded';
import ChecklistRtlRoundedIcon from '@mui/icons-material/ChecklistRtlRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import { verifyTurnstileToken } from "../utils/turnstile";

const PROPERTY_TYPE_OPTIONS = [
  {
    value: "privathaus",
    label: "Einfamilienhaus",
    description: "Privathaus",
  },
  {
    value: "mehrfamilienhaus",
    label: "Mehrfamilienhaus",
    description: "Wohngebäude mit mehreren Parteien",
  },
  {
    value: "gewerbe",
    label: "Gewerbe",
    description: "Gewerbehalle, Büro oder Betriebsfläche",
  },
  {
    value: "landwirtschaft",
    label: "Landwirtschaft",
    description: "Landwirtschaftliche Gebäude und Flächen",
  },
] as const;

type PropertyType = (typeof PROPERTY_TYPE_OPTIONS)[number]["value"];

const ROOF_TYPE_OPTIONS = [
  {
    value: "satteldach",
    label: "Satteldach",
  },
  {
    value: "flachdach",
    label: "Flachdach",
  },
  {
    value: "walmdach",
    label: "Walmdach",
  },
  {
    value: "pultdach",
    label: "Pultdach",
  },
] as const;

type RoofType = (typeof ROOF_TYPE_OPTIONS)[number]["value"];

const ROOF_RENOVATED_OPTIONS = [
  { value: "yes", label: "Ja" },
  { value: "no", label: "Nein" },
] as const;

type RoofRenovatedStatus = (typeof ROOF_RENOVATED_OPTIONS)[number]["value"];

const ROOF_MATERIAL_OPTIONS = [
  { value: "ziegel", label: "Ziegel" },
  { value: "beton", label: "Beton" },
  { value: "blech", label: "Blech" },
  { value: "bitumen", label: "Bitumen" },
  { value: "schiefer", label: "Schiefer" },
] as const;

type RoofMaterial = (typeof ROOF_MATERIAL_OPTIONS)[number]["value"];

const ROOF_ORIENTATION_OPTIONS = [
  { value: "sued", label: "Süd" },
  { value: "ost", label: "Ost" },
  { value: "west", label: "West" },
  { value: "nord", label: "Nord" },
  { value: "mehrere", label: "Mehrere" },
] as const;

type RoofOrientation = (typeof ROOF_ORIENTATION_OPTIONS)[number]["value"];

const SHADING_OPTIONS = [
  { value: "keine", label: "Keine" },
  { value: "baeume", label: "Bäume" },
  { value: "nachbarhaus", label: "Nachbarhaus" },
  { value: "schornstein", label: "Schornstein" },
] as const;

type ShadingType = (typeof SHADING_OPTIONS)[number]["value"];

const YES_NO_OPTIONS = [
  { value: "yes", label: "Ja" },
  { value: "no", label: "Nein" },
] as const;

type YesNoValue = (typeof YES_NO_OPTIONS)[number]["value"];

const STORAGE_INTEREST_OPTIONS = [
  { value: "yes", label: "Ja" },
  { value: "no", label: "Nein" },
  { value: "beratung", label: "Beratung" },
] as const;

type StorageInterest = (typeof STORAGE_INTEREST_OPTIONS)[number]["value"];

const STORAGE_GOAL_OPTIONS = [
  { value: "stromkosten", label: "Stromkosten sparen" },
  { value: "notstrom", label: "Notstrom" },
  { value: "autarkie", label: "Autark sein" },
  { value: "umwelt", label: "Umwelt" },
] as const;

type StorageGoal = (typeof STORAGE_GOAL_OPTIONS)[number]["value"];

const HEATING_OPTIONS = [
  { value: "gas", label: "Gas" },
  { value: "oel", label: "Öl" },
  { value: "waermepumpe", label: "Wärmepumpe" },
  { value: "fernwaerme", label: "Fernwärme" },
  { value: "strom", label: "Strom" },
] as const;

type HeatingType = (typeof HEATING_OPTIONS)[number]["value"];

const HOT_WATER_OPTIONS = [
  { value: "gas", label: "Gas" },
  { value: "oel", label: "Öl" },
  { value: "strom", label: "Strom" },
  { value: "boiler", label: "Boiler" },
  { value: "durchlauferhitzer", label: "Durchlauferhitzer" },
] as const;

type HotWaterType = (typeof HOT_WATER_OPTIONS)[number]["value"];

const PURCHASE_MODE_OPTIONS = [
  { value: "kauf", label: "Kauf" },
  { value: "leasing", label: "Leasing" },
] as const;

type PurchaseMode = (typeof PURCHASE_MODE_OPTIONS)[number]["value"];

const MAX_ATTACHMENT_SIZE_BYTES = 7 * 1024 * 1024;

type OfferUploadKey = "invoiceFile" | "meterCabinetPhotoFile" | "roofPhotoFile" | "housePhotoFile";
type OfferUploadFieldName =
  | "invoiceFileName"
  | "meterCabinetPhotoFileName"
  | "roofPhotoFileName"
  | "housePhotoFileName";
type OfferUploadState = Partial<Record<OfferUploadKey, File>>;

type OfferFormValues = {
  propertyType: PropertyType | "";
  buildingYear: string;
  roofRenovated: RoofRenovatedStatus | "";
  roofType: RoofType | "";
  roofMaterial: RoofMaterial | "";
  roofOrientation: RoofOrientation | "";
  roofPitch: string;
  shading: ShadingType | "";
  householdPersons: string;
  annualConsumptionKwh: string;
  invoiceFileName: string;
  hasHeatPump: YesNoValue | "";
  hasAirConditioning: YesNoValue | "";
  hasElectricCar: YesNoValue | "";
  hasLargeConsumers: YesNoValue | "";
  heatingType: HeatingType | "";
  hotWaterType: HotWaterType | "";
  interestHotWaterPv: YesNoValue | "";
  storageInterest: StorageInterest | "";
  storageGoal: StorageGoal | "";
  interestBackup: YesNoValue | "";
  interestOffGrid: YesNoValue | "";
  meterCabinetInBasement: YesNoValue | "";
  storageSpaceAvailable: YesNoValue | "";
  meterCabinetPhotoFileName: string;
  roofPhotoFileName: string;
  housePhotoFileName: string;
  financingDesired: YesNoValue | "";
  fundingCheck: YesNoValue | "";
  kfwBafaInterest: YesNoValue | "";
  installmentPayment: YesNoValue | "";
  purchaseMode: PurchaseMode | "";
  privacyAccepted: boolean;
  wantsOffer: boolean;
  wantsAppointment: boolean;
  wantsCallback: boolean;
  name: string;
  email: string;
  description: string;
  turnstileToken: string;
};

const FIELD_NAMES: Record<string, string> = {
  propertyType: "Haustyp",
  buildingYear: "Baujahr",
  roofRenovated: "Dach saniert",
  roofType: "Dachart",
  roofMaterial: "Dachmaterial",
  roofOrientation: "Dachausrichtung",
  roofPitch: "Dachneigung",
  shading: "Verschattung",
  householdPersons: "Personen im Haushalt",
  annualConsumptionKwh: "Stromverbrauch",
  hasHeatPump: "Wärmepumpe vorhanden",
  hasAirConditioning: "Klimaanlage",
  hasElectricCar: "E-Auto",
  hasLargeConsumers: "Pool / Sauna / große Verbraucher",
  heatingType: "Aktuelle Heizung",
  hotWaterType: "Warmwasseraufbereitung",
  interestHotWaterPv: "Interesse an Warmwasser mit PV",
  storageInterest: "Stromspeicher",
  storageGoal: "Ziel für Speicher / Autarkie",
  interestBackup: "Notstrom / Backup",
  interestOffGrid: "Inselbetrieb",
  meterCabinetInBasement: "Zählerschrank im Keller",
  storageSpaceAvailable: "Platz für Speicher",
  privacyAccepted: "Datenschutz",
  wantsOffer: "Angebot gewünscht",
  name: "Name",
  email: "E-Mail",
  description: "Nachricht / Projektbeschreibung"
};

export default function AngebotV2() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [captchaKey, setCaptchaKey] = useState(0);
  const [errorDialog, setErrorDialog] = useState<{ open: boolean; title: React.ReactNode; message: React.ReactNode }>({ open: false, title: "", message: "" });
  const [activeStep, setActiveStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const formTopRef = useRef<HTMLDivElement>(null);
  const [offerUploads, setOfferUploads] = useState<OfferUploadState>({});
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";
  const requiresCaptcha = Boolean(siteKey);
  const text = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const propertyTypeError = t("offer.form.validation.propertyType");
  const propertyTypeValidationMessage =
    propertyTypeError === "offer.form.validation.propertyType"
      ? "Bitte waehlen Sie aus, fuer wen die Photovoltaikanlage geplant ist."
      : propertyTypeError;
  const buildingYearValidationMessage = text(
    "offer.form.validation.buildingYear",
    "Bitte geben Sie das Baujahr des Hauses an."
  );
  const roofRenovatedValidationMessage = text(
    "offer.form.validation.roofRenovated",
    "Bitte waehlen Sie aus, ob das Dach saniert ist."
  );
  const roofTypeError = t("offer.form.validation.roofType");
  const roofTypeValidationMessage =
    roofTypeError === "offer.form.validation.roofType"
      ? "Bitte waehlen Sie die Dachart aus."
      : roofTypeError;
  const roofMaterialValidationMessage = text(
    "offer.form.validation.roofMaterial",
    "Bitte waehlen Sie das Dachmaterial aus."
  );
  const roofOrientationValidationMessage = text(
    "offer.form.validation.roofOrientation",
    "Bitte waehlen Sie die Dachausrichtung aus."
  );
  const shadingValidationMessage = text(
    "offer.form.validation.shading",
    "Bitte waehlen Sie die Verschattung aus."
  );
  const householdPersonsValidationMessage = text(
    "offer.form.validation.householdPersons",
    "Bitte geben Sie die Anzahl der Personen im Haushalt an."
  );
  const annualConsumptionValidationMessage = text(
    "offer.form.validation.annualConsumptionKwh",
    "Bitte geben Sie den Stromverbrauch pro Jahr in kWh an."
  );
  const hasHeatPumpValidationMessage = text(
    "offer.form.validation.hasHeatPump",
    "Bitte waehlen Sie aus, ob eine Waermepumpe vorhanden ist."
  );
  const hasAirConditioningValidationMessage = text(
    "offer.form.validation.hasAirConditioning",
    "Bitte waehlen Sie aus, ob eine Klimaanlage vorhanden ist."
  );
  const hasElectricCarValidationMessage = text(
    "offer.form.validation.hasElectricCar",
    "Bitte waehlen Sie aus, ob ein Elektroauto vorhanden ist."
  );
  const hasLargeConsumersValidationMessage = text(
    "offer.form.validation.hasLargeConsumers",
    "Bitte waehlen Sie aus, ob Pool, Sauna oder grosse Verbraucher vorhanden sind."
  );
  const heatingTypeValidationMessage = text(
    "offer.form.validation.heatingType",
    "Bitte waehlen Sie die aktuelle Heizung aus."
  );
  const hotWaterTypeValidationMessage = text(
    "offer.form.validation.hotWaterType",
    "Bitte waehlen Sie die aktuelle Warmwasserart aus."
  );
  const interestHotWaterPvValidationMessage = text(
    "offer.form.validation.interestHotWaterPv",
    "Bitte waehlen Sie aus, ob Interesse an Warmwasser mit PV-Strom besteht."
  );
  const storageInterestValidationMessage = text(
    "offer.form.validation.storageInterest",
    "Bitte waehlen Sie aus, ob ein Stromspeicher gewuenscht ist."
  );
  const storageGoalValidationMessage = text(
    "offer.form.validation.storageGoal",
    "Bitte waehlen Sie ein Ziel fuer Speicher / Autarkie aus."
  );
  const interestBackupValidationMessage = text(
    "offer.form.validation.interestBackup",
    "Bitte waehlen Sie aus, ob Interesse an Notstrom / Backup besteht."
  );
  const interestOffGridValidationMessage = text(
    "offer.form.validation.interestOffGrid",
    "Bitte waehlen Sie aus, ob Interesse an Inselbetrieb besteht."
  );
  const meterCabinetInBasementValidationMessage = text(
    "offer.form.validation.meterCabinetInBasement",
    "Bitte waehlen Sie aus, ob sich der Zaehlerschrank im Keller befindet."
  );
  const storageSpaceAvailableValidationMessage = text(
    "offer.form.validation.storageSpaceAvailable",
    "Bitte waehlen Sie aus, ob Platz fuer einen Speicher vorhanden ist."
  );
  const meterCabinetPhotoValidationMessage = text(
    "offer.form.validation.meterCabinetPhotoFileName",
    "Bitte laden Sie ein Foto vom Zaehlerschrank hoch."
  );
  const roofPhotoValidationMessage = text(
    "offer.form.validation.roofPhotoFileName",
    "Bitte laden Sie ein Foto vom Dach hoch."
  );
  const housePhotoValidationMessage = text(
    "offer.form.validation.housePhotoFileName",
    "Bitte laden Sie ein Foto vom Haus hoch."
  );
  const financingDesiredValidationMessage = text(
    "offer.form.validation.financingDesired",
    "Bitte waehlen Sie aus, ob Finanzierung gewuenscht ist."
  );
  const fundingCheckValidationMessage = text(
    "offer.form.validation.fundingCheck",
    "Bitte waehlen Sie aus, ob Foerderung geprueft werden soll."
  );
  const kfwBafaInterestValidationMessage = text(
    "offer.form.validation.kfwBafaInterest",
    "Bitte waehlen Sie aus, ob Interesse an KfW / BAFA besteht."
  );
  const installmentPaymentValidationMessage = text(
    "offer.form.validation.installmentPayment",
    "Bitte waehlen Sie aus, ob Ratenzahlung gewuenscht ist."
  );
  const purchaseModeValidationMessage = text(
    "offer.form.validation.purchaseMode",
    "Bitte waehlen Sie Kauf oder Leasing aus."
  );
  const privacyAcceptedValidationMessage = text(
    "offer.form.validation.privacyAccepted",
    "Bitte akzeptieren Sie den Datenschutz, um fortzufahren."
  );
  const offerSchema = useMemo(
    () =>
      z.object({
        propertyType: z.string().min(1, propertyTypeValidationMessage),
        buildingYear: z.string().min(1, buildingYearValidationMessage),
        roofRenovated: z.string().min(1, roofRenovatedValidationMessage),
        roofType: z.string().min(1, roofTypeValidationMessage),
        roofMaterial: z.string().min(1, roofMaterialValidationMessage),
        roofOrientation: z.string().min(1, roofOrientationValidationMessage),
        roofPitch: z.string().optional(),
        shading: z.string().min(1, shadingValidationMessage),
        householdPersons: z.string().min(1, householdPersonsValidationMessage),
        annualConsumptionKwh: z.string().min(1, annualConsumptionValidationMessage),
        invoiceFileName: z.string().optional(),
        hasHeatPump: z.string().min(1, hasHeatPumpValidationMessage),
        hasAirConditioning: z.string().min(1, hasAirConditioningValidationMessage),
        hasElectricCar: z.string().min(1, hasElectricCarValidationMessage),
        hasLargeConsumers: z.string().min(1, hasLargeConsumersValidationMessage),
        heatingType: z.string().min(1, heatingTypeValidationMessage),
        hotWaterType: z.string().min(1, hotWaterTypeValidationMessage),
        interestHotWaterPv: z.string().min(1, interestHotWaterPvValidationMessage),
        storageInterest: z.string().min(1, storageInterestValidationMessage),
        storageGoal: z.string().min(1, storageGoalValidationMessage),
        interestBackup: z.string().min(1, interestBackupValidationMessage),
        interestOffGrid: z.string().min(1, interestOffGridValidationMessage),
        meterCabinetInBasement: z.string().min(1, meterCabinetInBasementValidationMessage),
        storageSpaceAvailable: z.string().min(1, storageSpaceAvailableValidationMessage),
        meterCabinetPhotoFileName: z.string().optional(),
        roofPhotoFileName: z.string().optional(),
        housePhotoFileName: z.string().optional(),
        financingDesired: z.string().optional(),
        fundingCheck: z.string().optional(),
        kfwBafaInterest: z.string().optional(),
        installmentPayment: z.string().optional(),
        purchaseMode: z.string().optional(),
        privacyAccepted: z.boolean().refine((value) => value, privacyAcceptedValidationMessage),
        wantsOffer: z.boolean(),
        wantsAppointment: z.boolean(),
        wantsCallback: z.boolean(),
        name: z.string().min(1, t("home.form.validation.name")),
        email: z
          .string()
          .min(1, t("home.form.validation.email"))
          .email(t("home.form.validation.emailInvalid")),
        description: z
          .string()
          .min(1, t("home.form.validation.description"))
          .min(20, t("home.form.validation.descriptionMin")),
        turnstileToken: requiresCaptcha
          ? z.string().min(1, t("common.captcha.validation"))
          : z.string().optional(),
      }),
    [
      t,
      requiresCaptcha,
      propertyTypeValidationMessage,
      buildingYearValidationMessage,
      roofRenovatedValidationMessage,
      roofTypeValidationMessage,
      roofMaterialValidationMessage,
      roofOrientationValidationMessage,
      shadingValidationMessage,
      householdPersonsValidationMessage,
      annualConsumptionValidationMessage,
      hasHeatPumpValidationMessage,
      hasAirConditioningValidationMessage,
      hasElectricCarValidationMessage,
      hasLargeConsumersValidationMessage,
      heatingTypeValidationMessage,
      hotWaterTypeValidationMessage,
      interestHotWaterPvValidationMessage,
      storageInterestValidationMessage,
      storageGoalValidationMessage,
      interestBackupValidationMessage,
      interestOffGridValidationMessage,
      meterCabinetInBasementValidationMessage,
      storageSpaceAvailableValidationMessage,
      meterCabinetPhotoValidationMessage,
      roofPhotoValidationMessage,
      housePhotoValidationMessage,
      financingDesiredValidationMessage,
      fundingCheckValidationMessage,
      kfwBafaInterestValidationMessage,
      installmentPaymentValidationMessage,
      purchaseModeValidationMessage,
      privacyAcceptedValidationMessage,
    ]
  );
  const {
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OfferFormValues>({
    resolver: zodResolver(offerSchema),
    defaultValues: {
      propertyType: "",
      buildingYear: "",
      roofRenovated: "",
      roofType: "",
      roofMaterial: "",
      roofOrientation: "",
      roofPitch: "",
      shading: "",
      householdPersons: "",
      annualConsumptionKwh: "",
      invoiceFileName: "",
      hasHeatPump: "",
      hasAirConditioning: "",
      hasElectricCar: "",
      hasLargeConsumers: "",
      heatingType: "",
      hotWaterType: "",
      interestHotWaterPv: "",
      storageInterest: "",
      storageGoal: "",
      interestBackup: "",
      interestOffGrid: "",
      meterCabinetInBasement: "",
      storageSpaceAvailable: "",
      meterCabinetPhotoFileName: "",
      roofPhotoFileName: "",
      housePhotoFileName: "",
      financingDesired: "",
      fundingCheck: "",
      kfwBafaInterest: "",
      installmentPayment: "",
      purchaseMode: "",
      privacyAccepted: false,
      wantsOffer: true,
      wantsAppointment: false,
      wantsCallback: false,
      name: "",
      email: "",
      description: "",
      turnstileToken: "",
    },
  });

  const bullets = useMemo(() => {
    const value = t("offer.bullets", { returnObjects: true });
    if (Array.isArray(value) && value.every((item) => typeof item === "string")) {
      return value as string[];
    }

    const benefits = t("offer.benefits", { returnObjects: true });
    if (Array.isArray(benefits)) {
      return benefits
        .map((item) => {
          if (typeof item === "string") return item;
          if (typeof item === "object" && item && "title" in item && typeof item.title === "string") {
            return item.title;
          }
          return "";
        })
        .filter(Boolean);
    }

    return [];
  }, [t]);

  const infoTitle = text("offer.info.title", "So erhalten Sie Ihr Angebot");
  const infoBody = text(
    "offer.info.body",
    "Wir pruefen Ihr Projekt, klaeren offene Punkte und senden Ihnen ein unverbindliches, individuelles Angebot."
  );
  const formTitle = text("offer.formTitle", "Angebot anfordern");
  const submitLabel = text("offer.form.buttons.submit", text("home.form.submit", "Anfrage senden"));
  const submitLoadingLabel = text(
    "offer.form.buttons.submitting",
    text("home.form.submitLoading", "Sende...")
  );
  const selectedPropertyType = watch("propertyType");
  const selectedPropertyTypeLabel =
    PROPERTY_TYPE_OPTIONS.find((option) => option.value === selectedPropertyType)?.label ?? "";
  const selectedBuildingYear = watch("buildingYear");
  const selectedRoofRenovated = watch("roofRenovated");
  const selectedRoofRenovatedLabel =
    ROOF_RENOVATED_OPTIONS.find((option) => option.value === selectedRoofRenovated)?.label ?? "";
  const selectedRoofType = watch("roofType");
  const selectedRoofTypeLabel = ROOF_TYPE_OPTIONS.find((option) => option.value === selectedRoofType)?.label ?? "";
  const selectedRoofMaterial = watch("roofMaterial");
  const selectedRoofMaterialLabel =
    ROOF_MATERIAL_OPTIONS.find((option) => option.value === selectedRoofMaterial)?.label ?? "";
  const selectedRoofOrientation = watch("roofOrientation");
  const selectedRoofOrientationLabel =
    ROOF_ORIENTATION_OPTIONS.find((option) => option.value === selectedRoofOrientation)?.label ?? "";
  const selectedRoofPitch = watch("roofPitch");
  const selectedShading = watch("shading");
  const selectedShadingLabel = SHADING_OPTIONS.find((option) => option.value === selectedShading)?.label ?? "";
  const selectedHouseholdPersons = watch("householdPersons");
  const selectedAnnualConsumptionKwh = watch("annualConsumptionKwh");
  const selectedInvoiceFileName = watch("invoiceFileName");
  const selectedHasHeatPump = watch("hasHeatPump");
  const selectedHasHeatPumpLabel = YES_NO_OPTIONS.find((option) => option.value === selectedHasHeatPump)?.label ?? "";
  const selectedHasAirConditioning = watch("hasAirConditioning");
  const selectedHasAirConditioningLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedHasAirConditioning)?.label ?? "";
  const selectedHasElectricCar = watch("hasElectricCar");
  const selectedHasElectricCarLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedHasElectricCar)?.label ?? "";
  const selectedHasLargeConsumers = watch("hasLargeConsumers");
  const selectedHasLargeConsumersLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedHasLargeConsumers)?.label ?? "";
  const selectedHeatingType = watch("heatingType");
  const selectedHeatingTypeLabel =
    HEATING_OPTIONS.find((option) => option.value === selectedHeatingType)?.label ?? "";
  const selectedHotWaterType = watch("hotWaterType");
  const selectedHotWaterTypeLabel =
    HOT_WATER_OPTIONS.find((option) => option.value === selectedHotWaterType)?.label ?? "";
  const selectedInterestHotWaterPv = watch("interestHotWaterPv");
  const selectedInterestHotWaterPvLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedInterestHotWaterPv)?.label ?? "";
  const selectedStorageInterest = watch("storageInterest");
  const selectedStorageInterestLabel =
    STORAGE_INTEREST_OPTIONS.find((option) => option.value === selectedStorageInterest)?.label ?? "";
  const selectedStorageGoal = watch("storageGoal");
  const selectedStorageGoalLabel =
    STORAGE_GOAL_OPTIONS.find((option) => option.value === selectedStorageGoal)?.label ?? "";
  const selectedInterestBackup = watch("interestBackup");
  const selectedInterestBackupLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedInterestBackup)?.label ?? "";
  const selectedInterestOffGrid = watch("interestOffGrid");
  const selectedInterestOffGridLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedInterestOffGrid)?.label ?? "";
  const selectedMeterCabinetInBasement = watch("meterCabinetInBasement");
  const selectedMeterCabinetInBasementLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedMeterCabinetInBasement)?.label ?? "";
  const selectedStorageSpaceAvailable = watch("storageSpaceAvailable");
  const selectedStorageSpaceAvailableLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedStorageSpaceAvailable)?.label ?? "";
  const selectedMeterCabinetPhotoFileName = watch("meterCabinetPhotoFileName");
  const selectedRoofPhotoFileName = watch("roofPhotoFileName");
  const selectedHousePhotoFileName = watch("housePhotoFileName");
  const selectedFinancingDesired = watch("financingDesired");
  const selectedFinancingDesiredLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedFinancingDesired)?.label ?? "";
  const selectedFundingCheck = watch("fundingCheck");
  const selectedFundingCheckLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedFundingCheck)?.label ?? "";
  const selectedKfwBafaInterest = watch("kfwBafaInterest");
  const selectedKfwBafaInterestLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedKfwBafaInterest)?.label ?? "";
  const selectedInstallmentPayment = watch("installmentPayment");
  const selectedInstallmentPaymentLabel =
    YES_NO_OPTIONS.find((option) => option.value === selectedInstallmentPayment)?.label ?? "";
  const selectedPurchaseMode = watch("purchaseMode");
  const selectedPurchaseModeLabel =
    PURCHASE_MODE_OPTIONS.find((option) => option.value === selectedPurchaseMode)?.label ?? "";
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  
  const stepBack = text("offer.form.back", "Zurueck");
  const updateOfferUpload = (
    event: ChangeEvent<HTMLInputElement>,
    fileNameField: OfferUploadFieldName,
    fileKey: OfferUploadKey
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      setValue(fileNameField, "", {
        shouldValidate: true,
        shouldDirty: true,
      });
      setOfferUploads((current) => ({ ...current, [fileKey]: undefined }));
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      toast.error("Datei zu gross. Bitte maximal 7 MB pro Anhang.");
      event.target.value = "";
      setValue(fileNameField, "", {
        shouldValidate: true,
        shouldDirty: true,
      });
      setOfferUploads((current) => ({ ...current, [fileKey]: undefined }));
      return;
    }

    setValue(fileNameField, file.name, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setOfferUploads((current) => ({ ...current, [fileKey]: file }));
  };
  const selectedUploadFiles = useMemo(
    () => Object.values(offerUploads).filter((file): file is File => Boolean(file)),
    [offerUploads]
  );
  const selectedUploadCount = selectedUploadFiles.length;
  const selectedUploadTotalMb = (selectedUploadFiles.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024)).toFixed(2);

  return (
    <>
    <Container sx={{ py: { xs: 4, md: 6 } }}>
      <Box className="hero-shell" sx={{ p: { xs: 4, md: 6 }, position: "relative", mb: { xs: 4, md: 6 }, overflow: "hidden" }}>
        <Box className="hero-glow" />
        <Grid container spacing={4} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
          <Grid item xs={12} lg={8} md={8}>
            <Stack spacing={2} sx={{ maxWidth: 680 }}>
              <Typography variant="h1">{t("offer.hero.title")}</Typography>
              <Typography variant="body1" sx={{ opacity: 0.85 }}>
                {t("offer.hero.subtitle")}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} lg={4} md={4} sx={{ display: { xs: "none", md: "block" } }}>
            <Box sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 10px 40px rgba(0,0,0,0.15)" }}>
              <img
                src="/assets/van_front_right_view_1772340994888.png"
                alt="RSS-Energie Dienstfahrzeug"
                loading="lazy"
                style={{ width: "100%", height: "auto", display: "block", objectFit: "cover", aspectRatio: "16/10" }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} lg={4} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: "1px solid var(--border)", background: "var(--surface)" }}>
            <Stack spacing={2}>
              <Typography variant="h2">{infoTitle}</Typography>
              <Typography variant="body1" color="text.secondary">
                {infoBody}
              </Typography>
              <Stack spacing={1.5}>
                {bullets.map((bullet) => (
                  <Stack key={bullet} direction="row" spacing={1} alignItems="center">
                    <CheckCircleRoundedIcon color="success" fontSize="small" />
                    <Typography variant="body2">{bullet}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={8} md={8}>
          <Paper ref={formTopRef} elevation={0} sx={{ p: 3, border: "1px solid var(--border)", background: "var(--surface)" }}>
            <Stack
              spacing={2}
              component="form"
              onSubmit={handleSubmit(
                async (values) => {
                if (requiresCaptcha) {
                  const ok = await verifyTurnstileToken(values.turnstileToken);
                  if (!ok) {
                    toast.error(t("common.captcha.error"));
                    setValue("turnstileToken", "");
                    setCaptchaKey((value) => value + 1);
                    return;
                  }
                }
                const message = [
                  `Haustyp: ${PROPERTY_TYPE_OPTIONS.find((option) => option.value === values.propertyType)?.label ??
                  values.propertyType
                  }`,
                  `Baujahr Haus: ${values.buildingYear}`,
                  `Dach saniert: ${ROOF_RENOVATED_OPTIONS.find((option) => option.value === values.roofRenovated)?.label ??
                  values.roofRenovated
                  }`,
                  `Dachart: ${ROOF_TYPE_OPTIONS.find((option) => option.value === values.roofType)?.label ??
                  values.roofType
                  }`,
                  `Dachmaterial: ${ROOF_MATERIAL_OPTIONS.find((option) => option.value === values.roofMaterial)?.label ??
                  values.roofMaterial
                  }`,
                  `Dachausrichtung: ${ROOF_ORIENTATION_OPTIONS.find((option) => option.value === values.roofOrientation)?.label ??
                  values.roofOrientation
                  }`,
                  `Verschattung vorhanden: ${SHADING_OPTIONS.find((option) => option.value === values.shading)?.label ?? values.shading
                  }`,
                  `Dachneigung (optional): ${values.roofPitch || "-"}`,
                  `Personen im Haushalt: ${values.householdPersons}`,
                  `Stromverbrauch pro Jahr (kWh): ${values.annualConsumptionKwh}`,
                  `Letzte Stromrechnung (Dateiname): ${values.invoiceFileName}`,
                  `Waermepumpe vorhanden: ${YES_NO_OPTIONS.find((option) => option.value === values.hasHeatPump)?.label ??
                  values.hasHeatPump
                  }`,
                  `Klimaanlage vorhanden: ${YES_NO_OPTIONS.find((option) => option.value === values.hasAirConditioning)?.label ??
                  values.hasAirConditioning
                  }`,
                  `Elektroauto vorhanden: ${YES_NO_OPTIONS.find((option) => option.value === values.hasElectricCar)?.label ??
                  values.hasElectricCar
                  }`,
                  `Pool / Sauna / grosse Verbraucher: ${YES_NO_OPTIONS.find((option) => option.value === values.hasLargeConsumers)?.label ??
                  values.hasLargeConsumers
                  }`,
                  `Heizung: ${HEATING_OPTIONS.find((option) => option.value === values.heatingType)?.label ??
                  values.heatingType
                  }`,
                  `Warmwasser: ${HOT_WATER_OPTIONS.find((option) => option.value === values.hotWaterType)?.label ??
                  values.hotWaterType
                  }`,
                  `Interesse an Warmwasser mit PV-Strom: ${YES_NO_OPTIONS.find((option) => option.value === values.interestHotWaterPv)?.label ??
                  values.interestHotWaterPv
                  }`,
                  `Moechten Sie Stromspeicher: ${STORAGE_INTEREST_OPTIONS.find((option) => option.value === values.storageInterest)?.label ??
                  values.storageInterest
                  }`,
                  `Ziel fuer Speicher / Autarkie: ${STORAGE_GOAL_OPTIONS.find((option) => option.value === values.storageGoal)?.label ??
                  values.storageGoal
                  }`,
                  `Interesse an Notstrom / Backup: ${YES_NO_OPTIONS.find((option) => option.value === values.interestBackup)?.label ??
                  values.interestBackup
                  }`,
                  `Interesse an Inselbetrieb: ${YES_NO_OPTIONS.find((option) => option.value === values.interestOffGrid)?.label ??
                  values.interestOffGrid
                  }`,
                  `Zaehlerschrank im Keller: ${YES_NO_OPTIONS.find((option) => option.value === values.meterCabinetInBasement)?.label ??
                  values.meterCabinetInBasement
                  }`,
                  `Platz fuer Speicher vorhanden: ${YES_NO_OPTIONS.find((option) => option.value === values.storageSpaceAvailable)?.label ??
                  values.storageSpaceAvailable
                  }`,
                  `Foto Zaehlerschrank (Dateiname): ${values.meterCabinetPhotoFileName}`,
                  `Foto Dach (Dateiname): ${values.roofPhotoFileName}`,
                  `Foto Haus (Dateiname): ${values.housePhotoFileName}`,
                  `Finanzierung gewuenscht: ${YES_NO_OPTIONS.find((option) => option.value === values.financingDesired)?.label ??
                  values.financingDesired
                  }`,
                  `Foerderung pruefen: ${YES_NO_OPTIONS.find((option) => option.value === values.fundingCheck)?.label ??
                  values.fundingCheck
                  }`,
                  `KfW / BAFA Interesse: ${YES_NO_OPTIONS.find((option) => option.value === values.kfwBafaInterest)?.label ??
                  values.kfwBafaInterest
                  }`,
                  `Ratenzahlung: ${YES_NO_OPTIONS.find((option) => option.value === values.installmentPayment)?.label ??
                  values.installmentPayment
                  }`,
                  `Kauf oder Leasing: ${PURCHASE_MODE_OPTIONS.find((option) => option.value === values.purchaseMode)?.label ??
                  values.purchaseMode
                  }`,
                  `Datenschutz akzeptiert: ${values.privacyAccepted ? "Ja" : "Nein"}`,
                  `Angebot gewuenscht: ${values.wantsOffer ? "Ja" : "Nein"}`,
                  `Termin gewuenscht: ${values.wantsAppointment ? "Ja" : "Nein"}`,
                  `Rueckruf gewuenscht: ${values.wantsCallback ? "Ja" : "Nein"}`,
                  `Name: ${values.name}`,
                  `E-Mail: ${values.email}`,
                  "",
                  values.description,
                ].join("\n");
                const files = [
                  offerUploads.invoiceFile,
                  offerUploads.meterCabinetPhotoFile,
                  offerUploads.roofPhotoFile,
                  offerUploads.housePhotoFile,
                ].filter((file): file is File => Boolean(file));
                let attachments: Awaited<ReturnType<typeof buildContactAttachments>> | undefined;
                try {
                  attachments = files.length > 0 ? await buildContactAttachments(files) : undefined;
                } catch {
                  toast.error("Dateianhaenge konnten nicht verarbeitet werden.");
                  return;
                }
                toast.loading(t("offer.toast.sending"), { id: "offer" });
                let result;
                try {
                  result = await submitContact({
                    name: values.name,
                    email: values.email,
                    subject: "Angebotsanfrage",
                    message,
                    attachments,
                  });
                } catch (e) {
                  toast.dismiss("offer");
                  setErrorDialog({
                    open: true,
                    title: "Netzwerkfehler",
                    message: <Typography>Es gab ein Problem mit der Netzwerkverbindung. Bitte versuchen Sie es spaeter erneut.</Typography>
                  });
                  return;
                }
                
                if (!result.ok) {
                  toast.dismiss("offer");
                  setErrorDialog({
                    open: true,
                    title: "Übertragungsfehler",
                    message: <Typography>{result.status === 429 ? "Zu viele Anfragen. Bitte in einigen Minuten erneut versuchen." : "Das Formular konnte nicht gesendet werden. Bitte versuchen Sie es spaeter erneut."}</Typography>
                  });
                  return;
                }
                toast.success(t("offer.toast.success"), { id: "offer" });
                reset();
                setOfferUploads({});
                setActiveStep(1);
                setCaptchaKey((value) => value + 1);
                navigate("/nachricht-gesendet?type=angebot");
              },
              (errors) => {
                console.error("Form validation errors:", errors);
                setErrorDialog({
                  open: true,
                  title: "Unvollständige Angaben",
                  message: (
                    <Stack spacing={1.5}>
                      <Typography>Bitte ueberpruefen Sie Ihre Eingaben. Es fehlen noch Angaben in folgenden Feldern:</Typography>
                      <Box component="ul" sx={{ pl: 3, m: 0 }}>
                        {Object.keys(errors).map(key => (
                          <Typography component="li" key={key} fontWeight={600} color="error.main">
                            {FIELD_NAMES[key] || key}
                          </Typography>
                        ))}
                      </Box>
                    </Stack>
                  )
                });
              }
            )}
            >
              <Typography variant="h3">{formTitle}</Typography>
              

              
<Stepper activeStep={activeStep - 1} sx={{ mb: 4, "& .MuiStepLabel-label": { display: { xs: "none", md: "block" } } }} alternativeLabel>
  <Step><StepButton onClick={() => {
    setActiveStep(1);
    setTimeout(() => {
      const element = formTopRef.current;
      if (element) {
        const y = element.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({top: y, behavior: 'smooth'});
      }
    }, 50);
  }}>Gebäude & Dach</StepButton></Step>
  <Step><StepButton onClick={() => {
    setActiveStep(2);
    setTimeout(() => {
      const element = formTopRef.current;
      if (element) {
        const y = element.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({top: y, behavior: 'smooth'});
      }
    }, 50);
  }}>Verbrauch & Technik</StepButton></Step>
  <Step><StepButton onClick={() => {
    setActiveStep(3);
    setTimeout(() => {
      const element = formTopRef.current;
      if (element) {
        const y = element.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({top: y, behavior: 'smooth'});
      }
    }, 50);
  }}>Speicher & Autarkie</StepButton></Step>
  <Step><StepButton onClick={() => {
    setActiveStep(4);
    setTimeout(() => {
      const element = formTopRef.current;
      if (element) {
        const y = element.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({top: y, behavior: 'smooth'});
      }
    }, 50);
  }}>Installation</StepButton></Step>
  <Step><StepButton onClick={() => {
    setActiveStep(5);
    setTimeout(() => {
      const element = formTopRef.current;
      if (element) {
        const y = element.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({top: y, behavior: 'smooth'});
      }
    }, 50);
  }}>Kontaktdaten</StepButton></Step>
</Stepper>

{activeStep === 1 ? (
  <Stack spacing={4}>
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}><HomeRoundedIcon fontSize="large" sx={{ color: "var(--accent-cool)" }} /><Typography variant="h5" fontWeight={700}>Gebäude & Dach</Typography></Stack>
                <Controller
                    control={control}
                    name="propertyType"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Haustyp</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {PROPERTY_TYPE_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </Stack>
                    )}
                  />
                  <Typography variant="caption" color={errors.propertyType ? "error" : "text.secondary"}>
                    {errors.propertyType?.message ?? "Bitte waehlen Sie eine Option aus."}
                  </Typography>
<TextField
                    label="Baujahr Haus"
                    size="small"
                    fullWidth
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    error={Boolean(errors.buildingYear)}
                    helperText={errors.buildingYear?.message}
                    {...register("buildingYear")}
                  />
                  <Controller
                    control={control}
                    name="roofRenovated"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Ist das Dach saniert?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {ROOF_RENOVATED_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </Stack>
                    )}
                  />
                  <Typography variant="caption" color={errors.roofRenovated ? "error" : "text.secondary"}>
                    {errors.roofRenovated?.message ?? "Bitte waehlen Sie eine Option aus."}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    
                    
                  </Stack>
<Controller
                    control={control}
                    name="roofType"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Dachform</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {ROOF_TYPE_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </Stack>
                    )}
                  />
                  <Typography variant="caption" color={errors.roofType ? "error" : "text.secondary"}>
                    {errors.roofType?.message ?? "Bitte waehlen Sie eine Option aus."}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    
                    
                  </Stack>
<Controller
                    control={control}
                    name="roofMaterial"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Dachmaterial</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {ROOF_MATERIAL_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </Stack>
                    )}
                  />
                  <Typography variant="caption" color={errors.roofMaterial ? "error" : "text.secondary"}>
                    {errors.roofMaterial?.message ?? "Bitte waehlen Sie eine Option aus."}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    
                    
                  </Stack>
<Controller
                    control={control}
                    name="roofOrientation"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Dachausrichtung</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {ROOF_ORIENTATION_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </Stack>
                    )}
                  />
                  <Typography variant="caption" color={errors.roofOrientation ? "error" : "text.secondary"}>
                    {errors.roofOrientation?.message ?? "Bitte waehlen Sie eine Option aus."}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    
                    
                  </Stack>
<TextField
                    label="Dachneigung (optional)"
                    size="small"
                    fullWidth
                    inputProps={{ inputMode: "decimal" }}
                    {...register("roofPitch")}
                  />
                  <Controller
                    control={control}
                    name="shading"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Verschattung vorhanden?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {SHADING_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </Stack>
                    )}
                  />
                  <Typography variant="caption" color={errors.shading ? "error" : "text.secondary"}>
                    {errors.shading?.message ?? "Bitte waehlen Sie eine Option aus."}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    
                    
                  </Stack>
    <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
      
      <Button variant="contained" onClick={async () => { const valid = await trigger(["propertyType","buildingYear","roofRenovated","roofType","roofMaterial","roofOrientation","roofPitch","shading"]); if (!valid) return; setActiveStep(2);
                      setTimeout(() => {
                        const yOffset = -80;
                        const element = formTopRef.current;
                        if (element) {
                          const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
                          window.scrollTo({top: y, behavior: 'smooth'});
                        }
                      }, 50);
                    }}>Weiter</Button>
      
    </Stack>
  </Stack>
) : null}


{activeStep === 2 ? (
  <Stack spacing={4}>
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}><ElectricBoltRoundedIcon fontSize="large" sx={{ color: "var(--accent-cool)" }} /><Typography variant="h5" fontWeight={700}>Verbrauch & Technik</Typography></Stack>
                <Typography variant="body2" color="text.secondary">
                    Wichtig fuer die Groesse und Auslegung der Anlage.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Anhaenge: {selectedUploadCount}/4 | Gesamtgroesse: {selectedUploadTotalMb} MB
                  </Typography>
                  <TextField
                    label="Personen im Haushalt"
                    size="small"
                    fullWidth
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    error={Boolean(errors.householdPersons)}
                    helperText={errors.householdPersons?.message}
                    {...register("householdPersons")}
                  />
                  <TextField
                    label="Stromverbrauch pro Jahr (kWh)"
                    size="small"
                    fullWidth
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    error={Boolean(errors.annualConsumptionKwh)}
                    helperText={errors.annualConsumptionKwh?.message}
                    {...register("annualConsumptionKwh")}
                  />
                  <Stack spacing={0.8}>
                    <Typography variant="body2" fontWeight={600}>
                      Letzte Stromrechnung hochladen
                    </Typography>
                    <Button variant="outlined" color="inherit" component="label">
                      Datei auswaehlen
                      <input
                        hidden
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        onChange={(event) => {
                          updateOfferUpload(event, "invoiceFileName", "invoiceFile");
                        }}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {selectedInvoiceFileName || "Keine Datei ausgewaehlt"}
                    </Typography>
                    <Typography variant="caption" color={errors.invoiceFileName ? "error" : "text.secondary"}>
                      {errors.invoiceFileName?.message ?? "Erlaubte Formate: PDF, PNG, JPG, WEBP (max. 7 MB)"}
                    </Typography>
                  </Stack>
                  <Controller
                    control={control}
                    name="hasHeatPump"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Wärmepumpe vorhanden?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {YES_NO_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.hasHeatPump ? "error" : "text.secondary"}>
                          {errors.hasHeatPump?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Controller
                    control={control}
                    name="hasAirConditioning"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Klimaanlage vorhanden?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {YES_NO_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.hasAirConditioning ? "error" : "text.secondary"}>
                          {errors.hasAirConditioning?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Controller
                    control={control}
                    name="hasElectricCar"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Elektroauto vorhanden?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {YES_NO_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.hasElectricCar ? "error" : "text.secondary"}>
                          {errors.hasElectricCar?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Controller
                    control={control}
                    name="hasLargeConsumers"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Pool, Sauna oder große Verbraucher vorhanden?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {YES_NO_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.hasLargeConsumers ? "error" : "text.secondary"}>
                          {errors.hasLargeConsumers?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    
                    
                  </Stack>
<Typography variant="body2" color="text.secondary">
                    Wichtig fuer PV, Speicher und moegliche Warmwasser-Nutzung.
                  </Typography>
                  <Controller
                    control={control}
                    name="heatingType"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Aktuelle Heizung</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {HEATING_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.heatingType ? "error" : "text.secondary"}>
                          {errors.heatingType?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Controller
                    control={control}
                    name="hotWaterType"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Aktuelle Warmwasseraufbereitung</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {HOT_WATER_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.hotWaterType ? "error" : "text.secondary"}>
                          {errors.hotWaterType?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Controller
                    control={control}
                    name="interestHotWaterPv"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Interesse an Warmwasser mit PV-Strom?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {YES_NO_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.interestHotWaterPv ? "error" : "text.secondary"}>
                          {errors.interestHotWaterPv?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    
                    
                  </Stack>
    <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
      <Button variant="outlined" onClick={() => {
        setActiveStep(1);
        setTimeout(() => {
          const yOffset = -80;
          const element = formTopRef.current;
          if (element) {
            const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({top: y, behavior: 'smooth'});
          }
        }, 50);
      }}>{stepBack}</Button>
      <Button variant="contained" onClick={async () => { const valid = await trigger(["householdPersons","annualConsumptionKwh","invoiceFileName","hasHeatPump","hasAirConditioning","hasElectricCar","hasLargeConsumers","heatingType","hotWaterType","interestHotWaterPv"]); if (!valid) return; setActiveStep(3);
                      setTimeout(() => {
                        const yOffset = -80;
                        const element = formTopRef.current;
                        if (element) {
                          const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
                          window.scrollTo({top: y, behavior: 'smooth'});
                        }
                      }, 50);
                    }}>Weiter</Button>
      
    </Stack>
  </Stack>
) : null}


{activeStep === 3 ? (
  <Stack spacing={4}>
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}><BatteryChargingFullRoundedIcon fontSize="large" sx={{ color: "var(--accent-cool)" }} /><Typography variant="h5" fontWeight={700}>Speicher & Autarkie</Typography></Stack>
                <Typography variant="body2" color="text.secondary">
                    Hier beraten wir aktiv zum passenden Speicherkonzept.
                  </Typography>
                  <Controller
                    control={control}
                    name="storageInterest"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Möchten Sie einen Stromspeicher?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {STORAGE_INTEREST_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.storageInterest ? "error" : "text.secondary"}>
                          {errors.storageInterest?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Controller
                    control={control}
                    name="storageGoal"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Ziel für Speicher / Autarkie</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {STORAGE_GOAL_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.storageGoal ? "error" : "text.secondary"}>
                          {errors.storageGoal?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Controller
                    control={control}
                    name="interestBackup"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Interesse an Notstrom / Backup?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {YES_NO_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.interestBackup ? "error" : "text.secondary"}>
                          {errors.interestBackup?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Controller
                    control={control}
                    name="interestOffGrid"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Interesse an Inselbetrieb?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {YES_NO_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.interestOffGrid ? "error" : "text.secondary"}>
                          {errors.interestOffGrid?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    
                    
                  </Stack>
    <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
      <Button variant="outlined" onClick={() => {
        setActiveStep(2);
        setTimeout(() => {
          const yOffset = -80;
          const element = formTopRef.current;
          if (element) {
            const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({top: y, behavior: 'smooth'});
          }
        }, 50);
      }}>{stepBack}</Button>
      <Button variant="contained" onClick={async () => { const valid = await trigger(["storageInterest","storageGoal","interestBackup","interestOffGrid"]); if (!valid) return; setActiveStep(4);
                      setTimeout(() => {
                        const yOffset = -80;
                        const element = formTopRef.current;
                        if (element) {
                          const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
                          window.scrollTo({top: y, behavior: 'smooth'});
                        }
                      }, 50);
                    }}>Weiter</Button>
      
    </Stack>
  </Stack>
) : null}


{activeStep === 4 ? (
  <Stack spacing={4}>
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}><HandymanRoundedIcon fontSize="large" sx={{ color: "var(--accent-cool)" }} /><Typography variant="h5" fontWeight={700}>Installation</Typography></Stack>
                <Typography variant="body2" color="text.secondary">
                    Sehr wichtig fuer ein praezises Angebot.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Anhaenge: {selectedUploadCount}/4 | Gesamtgroesse: {selectedUploadTotalMb} MB
                  </Typography>
                  <Controller
                    control={control}
                    name="meterCabinetInBasement"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Befindet sich der Zählerschrank im Keller?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {YES_NO_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography
                          variant="caption"
                          color={errors.meterCabinetInBasement ? "error" : "text.secondary"}
                        >
                          {errors.meterCabinetInBasement?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Controller
                    control={control}
                    name="storageSpaceAvailable"
                    render={({ field }) => (
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>Platz für Speicher vorhanden?</Typography>
                        <RadioGroup
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        >
                          <Grid container spacing={1}>
                            {YES_NO_OPTIONS.map((option) => (
                              <Grid item xs={12} sm={6} key={option.value}>
                                <FormControlLabel
                                  value={option.value}
                                  control={<Radio color="primary" sx={{ mt: -0.5 }} />}
                                  label={
                                    <Box sx={{ py: 0.5 }}>
                                      <Typography variant="body1">{option.label}</Typography>
                                      {(option as any).description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                                          {(option as any).description}
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                        <Typography variant="caption" color={errors.storageSpaceAvailable ? "error" : "text.secondary"}>
                          {errors.storageSpaceAvailable?.message ?? "Bitte waehlen Sie eine Option aus."}
                        </Typography>
                      </Stack>
                    )}
                  />
                  <Stack spacing={0.8}>
                    <Typography variant="body2" fontWeight={600}>Foto vom Zaehlerschrank hochladen (optional)</Typography>
                    <Button variant="outlined" color="inherit" component="label">
                      Datei auswaehlen
                      <input
                        hidden
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        onChange={(event) => {
                          updateOfferUpload(event, "meterCabinetPhotoFileName", "meterCabinetPhotoFile");
                        }}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {selectedMeterCabinetPhotoFileName || "Keine Datei ausgewaehlt"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={errors.meterCabinetPhotoFileName ? "error" : "text.secondary"}
                    >
                      {errors.meterCabinetPhotoFileName?.message ?? "Erlaubte Formate: PDF, PNG, JPG, WEBP (max. 7 MB)"}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.8}>
                    <Typography variant="body2" fontWeight={600}>
                      Foto vom Dach hochladen
                    </Typography>
                    <Button variant="outlined" color="inherit" component="label">
                      Datei auswaehlen
                      <input
                        hidden
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        onChange={(event) => {
                          updateOfferUpload(event, "roofPhotoFileName", "roofPhotoFile");
                        }}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {selectedRoofPhotoFileName || "Keine Datei ausgewaehlt"}
                    </Typography>
                    <Typography variant="caption" color={errors.roofPhotoFileName ? "error" : "text.secondary"}>
                      {errors.roofPhotoFileName?.message ?? "Erlaubte Formate: PDF, PNG, JPG, WEBP (max. 7 MB)"}
                    </Typography>
                  </Stack>
                  <Stack spacing={0.8}>
                    <Typography variant="body2" fontWeight={600}>
                      Foto vom Haus hochladen
                    </Typography>
                    <Button variant="outlined" color="inherit" component="label">
                      Datei auswaehlen
                      <input
                        hidden
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.webp"
                        onChange={(event) => {
                          updateOfferUpload(event, "housePhotoFileName", "housePhotoFile");
                        }}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {selectedHousePhotoFileName || "Keine Datei ausgewaehlt"}
                    </Typography>
                    <Typography variant="caption" color={errors.housePhotoFileName ? "error" : "text.secondary"}>
                      {errors.housePhotoFileName?.message ?? "Erlaubte Formate: PDF, PNG, JPG, WEBP (max. 7 MB)"}
                    </Typography>
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                    
                    
                  </Stack>
    <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
      <Button variant="outlined" onClick={() => {
        setActiveStep(3);
        setTimeout(() => {
          const yOffset = -80;
          const element = formTopRef.current;
          if (element) {
            const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({top: y, behavior: 'smooth'});
          }
        }, 50);
      }}>{stepBack}</Button>
      <Button variant="contained" onClick={async () => { const valid = await trigger(["meterCabinetInBasement","storageSpaceAvailable","meterCabinetPhotoFileName"]); if (!valid) return; setActiveStep(5);
                      setTimeout(() => {
                        const yOffset = -80;
                        const element = formTopRef.current;
                        if (element) {
                          const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
                          window.scrollTo({top: y, behavior: 'smooth'});
                        }
                      }, 50);
                    }}>Weiter</Button>
      
    </Stack>
  </Stack>
) : null}


{activeStep === 5 ? (
  <Stack spacing={4}>
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: -1 }}><ChecklistRtlRoundedIcon fontSize="medium" sx={{ color: "var(--accent-cool)" }} /><Typography variant="h5">Ihre Angaben im Ueberblick</Typography></Stack>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
    <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Haustyp
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedPropertyTypeLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Baujahr Haus
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedBuildingYear}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Dach saniert
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedRoofRenovatedLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Dachart
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedRoofTypeLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Dachmaterial
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedRoofMaterialLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Dachausrichtung
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedRoofOrientationLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Verschattung vorhanden
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedShadingLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Dachneigung (optional)
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedRoofPitch || "-"}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Personen im Haushalt
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedHouseholdPersons}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Stromverbrauch pro Jahr (kWh)
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedAnnualConsumptionKwh}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Letzte Stromrechnung
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedInvoiceFileName}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Waermepumpe vorhanden
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedHasHeatPumpLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Klimaanlage vorhanden
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedHasAirConditioningLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Elektroauto vorhanden
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedHasElectricCarLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Pool / Sauna / grosse Verbraucher
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedHasLargeConsumersLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Heizung
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedHeatingTypeLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Warmwasser
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedHotWaterTypeLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Interesse an Warmwasser mit PV-Strom
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedInterestHotWaterPvLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Moechten Sie Stromspeicher
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedStorageInterestLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Ziel
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedStorageGoalLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Interesse an Notstrom / Backup
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedInterestBackupLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Interesse an Inselbetrieb
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedInterestOffGridLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Zaehlerschrank im Keller
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedMeterCabinetInBasementLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Platz fuer Speicher vorhanden
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedStorageSpaceAvailableLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Foto Zaehlerschrank
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedMeterCabinetPhotoFileName}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Foto Dach
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedRoofPhotoFileName}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Foto Haus
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedHousePhotoFileName}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Finanzierung gewuenscht
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedFinancingDesiredLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Foerderung pruefen
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedFundingCheckLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      KfW / BAFA Interesse
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedKfwBafaInterestLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Ratenzahlung
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedInstallmentPaymentLabel}
                    </Typography>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.2,
                      border: "1px solid var(--border)",
                      borderRadius: 2,
                      background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Kauf oder Leasing
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedPurchaseModeLabel}
                    </Typography>
                  </Paper>
                      </Box>
    <Divider sx={{ my: 1 }} />
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: -1 }}><SendRoundedIcon fontSize="medium" sx={{ color: "var(--accent-cool)" }} /><Typography variant="h5">Kontaktdaten & Abschluss</Typography></Stack>
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          border: "1px solid var(--border)",
          borderRadius: 2,
          background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Datenschutz
        </Typography>
        <Stack spacing={0.2} sx={{ mt: 0.5 }}>
          <Controller
            control={control}
            name="privacyAccepted"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(_, checked) => field.onChange(checked)}
                    sx={{ color: "var(--accent-cool)" }}
                  />
                }
                label="Datenschutz akzeptieren"
              />
            )}
          />
        </Stack>
        <Typography variant="caption" color={errors.privacyAccepted ? "error" : "text.secondary"}>
          {errors.privacyAccepted?.message ?? "Bitte akzeptieren Sie den Datenschutz, um fortzufahren."}
        </Typography>
      </Paper>
      
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          border: "1px solid var(--border)",
          borderRadius: 2,
          background: "color-mix(in srgb, var(--surface) 84%, var(--accent-cool) 16%)",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Weitere Optionen
        </Typography>
        <Stack spacing={0.2} sx={{ mt: 0.5 }}>
          <Controller
            control={control}
            name="wantsOffer"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(_, checked) => field.onChange(checked)}
                    sx={{ color: "var(--accent-cool)" }}
                  />
                }
                label="Angebot gewuenscht"
              />
            )}
          />
          <Controller
            control={control}
            name="wantsAppointment"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(_, checked) => field.onChange(checked)}
                    sx={{ color: "var(--accent-cool)" }}
                  />
                }
                label="Termin gewuenscht"
              />
            )}
          />
          <Controller
            control={control}
            name="wantsCallback"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(_, checked) => field.onChange(checked)}
                    sx={{ color: "var(--accent-cool)" }}
                  />
                }
                label="Rueckruf gewuenscht"
              />
            )}
          />
        </Stack>
      </Paper>

      <TextField
                    label={t("home.form.name")}
                    size="small"
                    fullWidth
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    {...register("name")}
                  />
                  <TextField
                    label={t("home.form.email")}
                    size="small"
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    {...register("email")}
                  />
                  <TextField
                    label={text("offer.form.message", t("home.form.description"))}
                    size="small"
                    fullWidth
                    multiline
                    minRows={4}
                    error={Boolean(errors.description)}
                    helperText={errors.description?.message}
                    {...register("description")}
                  />
                  {requiresCaptcha ? (
                    <Stack spacing={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {t("common.captcha.label")}
                      </Typography>
                      <Controller
                        control={control}
                        name="turnstileToken"
                        render={({ field }) => (
                          <TurnstileWidget
                            siteKey={siteKey}
                            resetKey={captchaKey}
                            onToken={(token) => field.onChange(token)}
                          />
                        )}
                      />
                      <Typography variant="caption" color={errors.turnstileToken ? "error" : "text.secondary"}>
                        {errors.turnstileToken?.message ?? t("common.captcha.helper")}
                      </Typography>
                    </Stack>
                  ) : null}

        </Stack>
    <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
      <Button variant="outlined" onClick={() => {
        setActiveStep(4);
        setTimeout(() => {
          const yOffset = -80;
          const element = formTopRef.current;
          if (element) {
            const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
            window.scrollTo({top: y, behavior: 'smooth'});
          }
        }, 50);
      }}>{stepBack}</Button>
      
      <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>{isSubmitting ? submitLoadingLabel : submitLabel}</Button>
    </Stack>
  </Stack>
) : null}

            
<input type="hidden" {...register("roofPhotoFileName")} value="-" />
<input type="hidden" {...register("housePhotoFileName")} value="-" />
<input type="hidden" {...register("financingDesired")} value="-" />
<input type="hidden" {...register("fundingCheck")} value="-" />
<input type="hidden" {...register("kfwBafaInterest")} value="-" />
<input type="hidden" {...register("installmentPayment")} value="-" />
<input type="hidden" {...register("purchaseMode")} value="-" />

            </Stack>
          </Paper>
        </Grid>

      </Grid>
    </Container>
    <Dialog open={errorDialog.open} onClose={() => setErrorDialog({ ...errorDialog, open: false })} maxWidth="xs" fullWidth>
      <DialogTitle>{errorDialog.title}</DialogTitle>
      <DialogContent>
        <Box>{errorDialog.message}</Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setErrorDialog({ ...errorDialog, open: false })} variant="contained" color="primary">Verstanden</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
